/**
 * Native Chart Slide Builder
 *
 * Builds a base64 PPTX slide containing a single native, editable PowerPoint
 * chart from a `ChartInfo` payload, using PptxGenJS. The resulting base64 is
 * inserted into the live presentation via Office.js `insertSlidesFromBase64`.
 *
 * This module is the Office.js half of the in-slide chart contract: the LLM
 * emits charts inline as `shape.action="create"` with a `chart: ChartInfo`
 * body, the chartTwoPassTranslator extracts those chart shapes for Office.js
 * mode, and this module turns each one into an inserted slide.
 *
 * In FactSet COM mode the chart payload is forwarded untouched through
 * PerformBatchPptOperations and never reaches this module.
 */

import PptxGenJS from "pptxgenjs";
import {
  ChartAxisInfo,
  ChartInfo,
  ChartSeriesInfo,
  XlAxisGroup,
  XlChartType,
  XlLegendPosition,
} from "../types";
import { guardedPowerPointRun, isOfficeJsBypassed } from "./officeJsGuard";
import { shouldUseFactSetBridge } from "./presentationHost";

/**
 * Map from XlChartType to PptxGenJS chart-name + barDir/grouping hints.
 */
const CHART_TYPE_MAP: Record<
  XlChartType,
  { name: PptxGenJS.CHART_NAME; barDir?: "bar" | "col"; barGrouping?: string; smooth?: boolean }
> = {
  xlColumnClustered: { name: "bar", barDir: "col", barGrouping: "clustered" },
  xlColumnStacked: { name: "bar", barDir: "col", barGrouping: "stacked" },
  xlColumnStacked100: { name: "bar", barDir: "col", barGrouping: "percentStacked" },
  xlBarClustered: { name: "bar", barDir: "bar", barGrouping: "clustered" },
  xlBarStacked: { name: "bar", barDir: "bar", barGrouping: "stacked" },
  xlBarStacked100: { name: "bar", barDir: "bar", barGrouping: "percentStacked" },
  xlLine: { name: "line" },
  xlLineMarkers: { name: "line" },
  xlLineStacked: { name: "line" },
  xlLineStackedMarkers: { name: "line" },
  xlPie: { name: "pie" },
  xlPieExploded: { name: "pie" },
  xlDoughnut: { name: "doughnut" },
  xlDoughnutExploded: { name: "doughnut" },
  xlArea: { name: "area" },
  xlAreaStacked: { name: "area" },
  xlAreaStacked100: { name: "area" },
  xlXYScatter: { name: "scatter" },
  xlXYScatterLines: { name: "scatter" },
  xlXYScatterLinesNoMarkers: { name: "scatter" },
  xlBubble: { name: "bubble" },
  xlBubble3DEffect: { name: "bubble" },
  xlRadar: { name: "radar" },
  xlRadarFilled: { name: "radar" },
};

const LEGEND_POSITION_MAP: Record<XlLegendPosition, string> = {
  xlLegendPositionTop: "t",
  xlLegendPositionBottom: "b",
  xlLegendPositionLeft: "l",
  xlLegendPositionRight: "r",
  xlLegendPositionCorner: "tr",
};

const DEFAULT_COLORS = [
  "0E6B5D",
  "58ADAF",
  "FF8282",
  "4A90D9",
  "F5A623",
  "7B68EE",
  "50C878",
  "FF6B6B",
];

const DEFAULT_LAYOUT_INCHES = {
  x: 0.5,
  y: 0.8,
  w: 9,
  h: 4.5,
};

const POINTS_PER_INCH = 72;

export interface NativeChartInsertResult {
  success: boolean;
  message: string;
  insertedSlideId?: string;
  insertedSlideIndex?: number;
}

export interface ChartSlideBuildResult {
  chartName: PptxGenJS.CHART_NAME;
  chartOpts: PptxGenJS.IChartOpts;
  chartData: PptxGenJS.OptsChartData[];
  resolvedTitle: string;
  isCombo: boolean;
  comboTypes?: PptxGenJS.IChartMulti[];
}

function parseJsonArray<T>(raw: string | undefined): T[] {
  if (typeof raw !== "string") {
    return [];
  }
  const trimmed = raw.trim();
  if (!trimmed) {
    return [];
  }
  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

function pointsToInches(pt: number): number {
  return pt / POINTS_PER_INCH;
}

function resolveLayoutInches(chart: ChartInfo): {
  x: number;
  y: number;
  w: number;
  h: number;
} {
  if (chart.position) {
    return {
      x: pointsToInches(chart.position.left),
      y: pointsToInches(chart.position.top),
      w: pointsToInches(chart.position.width),
      h: pointsToInches(chart.position.height),
    };
  }
  return { ...DEFAULT_LAYOUT_INCHES };
}

function resolveChartColors(chart: ChartInfo): string[] {
  const colors: string[] = [];
  for (const series of chart.series) {
    const color = series.fill?.colorRgb || series.marker?.fill?.colorRgb || series.line?.colorRgb;
    if (color) {
      colors.push(color);
    }
  }
  if (colors.length === 0) {
    return DEFAULT_COLORS.slice(0, Math.max(chart.series.length, 1));
  }
  // Fill missing trailing slots from the default palette.
  while (colors.length < chart.series.length) {
    colors.push(DEFAULT_COLORS[colors.length % DEFAULT_COLORS.length]);
  }
  return colors;
}

function categoriesForSeries(series: ChartSeriesInfo): (string | number)[] {
  const cats = parseJsonArray<string | number>(series.categories);
  return cats;
}

function valuesForSeries(series: ChartSeriesInfo): Array<number | null> {
  const values = parseJsonArray<number | null>(series.values);
  return values.map((value) => {
    if (value === null || value === undefined) {
      return null;
    }
    const num = typeof value === "number" ? value : Number(value);
    return Number.isFinite(num) ? num : null;
  });
}

function xValuesForSeries(series: ChartSeriesInfo): number[] {
  return parseJsonArray<number>(series.xValues).map((value) => {
    const num = typeof value === "number" ? value : Number(value);
    return Number.isFinite(num) ? num : 0;
  });
}

function bubbleSizesForSeries(series: ChartSeriesInfo): number[] {
  return parseJsonArray<number>(series.bubbleSizes).map((value) => {
    const num = typeof value === "number" ? value : Number(value);
    return Number.isFinite(num) ? num : 0;
  });
}

function findValueAxis(
  axes: ChartAxisInfo[] | undefined,
  group: XlAxisGroup
): ChartAxisInfo | undefined {
  return axes?.find((axis) => axis.axisType === "xlValue" && (axis.axisGroup || "xlPrimary") === group);
}

function findCategoryAxis(axes: ChartAxisInfo[] | undefined): ChartAxisInfo | undefined {
  return axes?.find((axis) => axis.axisType === "xlCategory");
}

function buildChartDataForSeries(
  chart: ChartInfo,
  series: ChartSeriesInfo,
  chartTypeName: PptxGenJS.CHART_NAME
): PptxGenJS.OptsChartData {
  if (chartTypeName === "scatter") {
    return {
      name: series.name,
      values: valuesForSeries(series),
      // PptxGenJS scatter expects labels as numeric x values.
      labels: xValuesForSeries(series),
    } as unknown as PptxGenJS.OptsChartData;
  }
  if (chartTypeName === "bubble") {
    return {
      name: series.name,
      values: valuesForSeries(series),
      labels: xValuesForSeries(series),
      sizes: bubbleSizesForSeries(series),
    } as unknown as PptxGenJS.OptsChartData;
  }
  const sharedCategories = categoriesForSeries(series);
  const fallbackCategories =
    sharedCategories.length > 0
      ? sharedCategories
      : categoriesForSeries(chart.series[0]);
  return {
    name: series.name,
    labels: fallbackCategories.map((cat) => (typeof cat === "string" ? cat : String(cat))),
    values: valuesForSeries(series),
  };
}

function isComboChart(chart: ChartInfo): boolean {
  return chart.series.some(
    (series) => series.chartType !== undefined && series.chartType !== chart.chartType
  );
}

function chartTypeNameForSeries(chart: ChartInfo, series: ChartSeriesInfo): PptxGenJS.CHART_NAME {
  const xlType = series.chartType || chart.chartType;
  return CHART_TYPE_MAP[xlType]?.name || "bar";
}

function buildBaseChartOpts(chart: ChartInfo, layout: { x: number; y: number; w: number; h: number }): PptxGenJS.IChartOpts {
  const opts: Record<string, unknown> = {
    x: layout.x,
    y: layout.y,
    w: layout.w,
    h: layout.h,
  };

  if (chart.title?.hasTitle !== false && chart.title?.text) {
    opts.showTitle = true;
    opts.title = chart.title.text;
    opts.titleAlign = "center";
    opts.titleFontSize = chart.title.font?.size ?? 14;
    if (chart.title.font?.colorRgb) opts.titleColor = chart.title.font.colorRgb;
    if (chart.title.font?.bold !== undefined) opts.titleBold = chart.title.font.bold;
    if (chart.title.font?.name) opts.titleFontFace = chart.title.font.name;
  } else {
    opts.showTitle = false;
  }

  // Always hide legends — chart title, axis titles, and data labels are sufficient.
  opts.showLegend = false;

  const baseMap = CHART_TYPE_MAP[chart.chartType];
  if (baseMap?.barDir) opts.barDir = baseMap.barDir;
  if (baseMap?.barGrouping) opts.barGrouping = baseMap.barGrouping;

  const primaryValueAxis = findValueAxis(chart.axes, "xlPrimary");
  if (primaryValueAxis && primaryValueAxis.enabled === false) {
    opts.valAxisHidden = true;
    opts.valGridLine = { style: "none" };
    opts.valAxisLineShow = false;
  } else if (primaryValueAxis) {
    if (primaryValueAxis.minimumScale !== undefined) opts.valAxisMinVal = primaryValueAxis.minimumScale;
    if (primaryValueAxis.maximumScale !== undefined) opts.valAxisMaxVal = primaryValueAxis.maximumScale;
    if (primaryValueAxis.majorUnit !== undefined) opts.valAxisMajorUnit = primaryValueAxis.majorUnit;
    if (primaryValueAxis.hasTitle && primaryValueAxis.titleText) {
      opts.showValAxisTitle = true;
      opts.valAxisTitle = primaryValueAxis.titleText;
    }
    if (primaryValueAxis.numberFormat) opts.valAxisLabelFormatCode = primaryValueAxis.numberFormat;
  } else {
    // No value axis specified — default to hidden (data labels preferred)
    opts.valAxisHidden = true;
    opts.valGridLine = { style: "none" };
    opts.valAxisLineShow = false;
  }
  const categoryAxis = findCategoryAxis(chart.axes);
  if (categoryAxis?.hasTitle && categoryAxis.titleText) {
    opts.showCatAxisTitle = true;
    opts.catAxisTitle = categoryAxis.titleText;
  }
  // Default: hide category axis gridlines and axis line for clean look
  if (!categoryAxis || categoryAxis.hasMajorGridlines === false) {
    opts.catGridLine = { style: "none" };
  }
  if (!categoryAxis || categoryAxis.axisLine?.isVisible === false) {
    opts.catAxisLineShow = false;
  }

  if (chart.chartType === "xlLineMarkers" || chart.chartType === "xlLineStackedMarkers") {
    opts.lineDataSymbol = "circle";
    opts.lineDataSymbolSize = 7;
  }

  if (chart.chartType === "xlDoughnut" || chart.chartType === "xlDoughnutExploded") {
    opts.holeSize = chart.doughnutHoleSize ?? 60;
  }
  if (chart.gapWidth !== undefined) opts.barGapWidthPct = chart.gapWidth;

  return opts as PptxGenJS.IChartOpts;
}

export function buildChartSlideFromChartInfo(chart: ChartInfo): ChartSlideBuildResult {
  const layout = resolveLayoutInches(chart);
  const chartColors = resolveChartColors(chart);
  const baseOpts = buildBaseChartOpts(chart, layout);
  (baseOpts as unknown as Record<string, unknown>).chartColors = chartColors;

  const resolvedTitle = chart.title?.text || chart.name || "Chart";

  if (isComboChart(chart)) {
    const comboTypes: PptxGenJS.IChartMulti[] = chart.series.map((series, idx) => {
      const seriesType = series.chartType || chart.chartType;
      const seriesMap = CHART_TYPE_MAP[seriesType] || CHART_TYPE_MAP[chart.chartType];
      const chartTypeName = seriesMap.name;
      const entryOpts: Record<string, unknown> = {};
      if (seriesMap.barDir) entryOpts.barDir = seriesMap.barDir;
      if (seriesMap.barGrouping) entryOpts.barGrouping = seriesMap.barGrouping;
      const seriesColor = chartColors[idx] || chartColors[chartColors.length - 1];
      if (seriesColor) entryOpts.chartColors = [seriesColor];
      if (series.axisGroup === "xlSecondary") {
        entryOpts.secondaryValAxis = true;
        entryOpts.secondaryCatAxis = true;
      }
      if (chartTypeName === "line" && (series.chartType === "xlLineMarkers" || series.chartType === "xlLineStackedMarkers")) {
        entryOpts.lineDataSymbol = series.marker?.style === "xlMarkerStyleNone" ? "none" : "circle";
        entryOpts.lineDataSymbolSize = series.marker?.size ?? 7;
        entryOpts.lineSize = series.line?.weight ?? 3;
      }
      return {
        type: chartTypeName,
        data: [buildChartDataForSeries(chart, series, chartTypeName)],
        options: entryOpts as PptxGenJS.IChartOpts,
      };
    });

    // Combo charts: when any series targets the secondary axis, ensure
    // matching valAxes / catAxes shape.
    if (comboTypes.some((entry) => (entry.options as Record<string, unknown>).secondaryValAxis === true)) {
      const baseRecord = baseOpts as unknown as Record<string, unknown>;
      const secondaryValAxis = findValueAxis(chart.axes, "xlSecondary");
      const valAxisPrimary = {} as Record<string, unknown>;
      const valAxisSecondary: Record<string, unknown> = {};
      if (secondaryValAxis?.minimumScale !== undefined) {
        valAxisSecondary.valAxisMinVal = secondaryValAxis.minimumScale;
      }
      if (secondaryValAxis?.maximumScale !== undefined) {
        valAxisSecondary.valAxisMaxVal = secondaryValAxis.maximumScale;
      }
      if (secondaryValAxis?.numberFormat) {
        valAxisSecondary.valAxisLabelFormatCode = secondaryValAxis.numberFormat;
      }
      if (secondaryValAxis?.hasTitle && secondaryValAxis.titleText) {
        valAxisSecondary.showValAxisTitle = true;
        valAxisSecondary.valAxisTitle = secondaryValAxis.titleText;
      }
      baseRecord.valAxes = [valAxisPrimary, valAxisSecondary];
      baseRecord.catAxes = [{}, { catAxisHidden: true }];
      delete baseRecord.barDir;
    }

    return {
      chartName: comboTypes[0]?.type || "bar",
      chartOpts: baseOpts,
      chartData: [],
      resolvedTitle,
      isCombo: true,
      comboTypes,
    };
  }

  const chartTypeName = chartTypeNameForSeries(chart, chart.series[0]);
  const data = chart.series.map((series) => buildChartDataForSeries(chart, series, chartTypeName));
  return {
    chartName: chartTypeName,
    chartOpts: baseOpts,
    chartData: data,
    resolvedTitle,
    isCombo: false,
  };
}

/**
 * Generate a single-slide PPTX containing the chart, returning the base64 payload.
 * Intended for Office.js `insertSlidesFromBase64`.
 */
export async function generateChartSlideBase64(chart: ChartInfo): Promise<string> {
  const pptx = new PptxGenJS();
  pptx.layout = "LAYOUT_16x9";
  pptx.author = shouldUseFactSetBridge() ? "FactSet AI" : "Finster AI";

  const build = buildChartSlideFromChartInfo(chart);
  pptx.title = build.resolvedTitle;

  const slide = pptx.addSlide();
  const slideAny = slide as unknown as {
    addChart: (
      chartType: PptxGenJS.CHART_NAME | PptxGenJS.IChartMulti[],
      dataOrOptions?: PptxGenJS.OptsChartData[] | PptxGenJS.IChartOpts,
      options?: PptxGenJS.IChartOpts
    ) => void;
  };

  if (build.isCombo && build.comboTypes && build.comboTypes.length > 0) {
    slideAny.addChart(build.comboTypes, build.chartOpts);
  } else {
    slideAny.addChart(build.chartName, build.chartData, build.chartOpts);
  }

  return (await pptx.write({ outputType: "base64" })) as string;
}

/**
 * Insert a chart slide built from ChartInfo into the live presentation via
 * Office.js. Used by chartTwoPassTranslator in Office.js mode. NOT used in
 * FactSet COM mode (charts flow through the intent bridge there).
 */
export async function insertChartSlideFromChartInfo(
  chart: ChartInfo,
  log: (msg: string) => void,
  insertAfterSlideId?: string
): Promise<NativeChartInsertResult> {
  if (isOfficeJsBypassed()) {
    log("[OfficeJS NOP] insertChartSlideFromChartInfo bypassed");
    return { success: true, message: "Bypassed (NOP mode)" };
  }
  try {
    log(`Generating native ${chart.chartType} chart: "${chart.title?.text || chart.name || "Untitled"}"`);
    if (chart.position) {
      log(
        `   Position (pt): left=${chart.position.left} top=${chart.position.top} ` +
          `width=${chart.position.width} height=${chart.position.height}`
      );
    }
    log(`   Series: ${chart.series.map((entry) => entry.name).join(", ") || "(none)"}`);

    const pptxBase64 = await generateChartSlideBase64(chart);
    log(`   Generated PPTX (${Math.round(pptxBase64.length / 1024)}KB)`);

    let insertedSlideId: string | undefined;
    let insertedSlideIndex: number | undefined;

    await guardedPowerPointRun(async (context) => {
      const slidesBefore = context.presentation.slides;
      slidesBefore.load("items/id");
      await context.sync();

      const beforeIds = new Set(slidesBefore.items.map((slide) => slide.id));
      const insertOptions: PowerPoint.InsertSlideOptions = {
        formatting: PowerPoint.InsertSlideFormatting.useDestinationTheme,
      };

      let targetSlideId = insertAfterSlideId;
      if (!targetSlideId) {
        const selectedSlides = context.presentation.getSelectedSlides();
        selectedSlides.load("items/id");
        await context.sync();
        if (selectedSlides.items.length > 0) {
          targetSlideId = selectedSlides.items[0].id;
        }
      }
      if (targetSlideId && slidesBefore.items.some((slide) => slide.id === targetSlideId)) {
        log(`   Inserting after slide: ${targetSlideId}`);
        insertOptions.targetSlideId = targetSlideId;
      } else {
        log(`   No target slide resolved, inserting at end`);
      }

      context.presentation.insertSlidesFromBase64(pptxBase64, insertOptions);
      await context.sync();

      const slidesAfter = context.presentation.slides;
      slidesAfter.load("items/id");
      await context.sync();

      const insertedSlide = slidesAfter.items.find((slide) => !beforeIds.has(slide.id));
      if (insertedSlide) {
        insertedSlideId = insertedSlide.id;
        insertedSlideIndex = slidesAfter.items.findIndex((slide) => slide.id === insertedSlide.id);
      }
      log(`   ✓ Inserted native chart slide`);
    });

    return {
      success: true,
      message: `Added new slide with native ${chart.chartType} chart: "${chart.title?.text || chart.name || "Chart"}"`,
      insertedSlideId,
      insertedSlideIndex,
    };
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    log(`   ✗ Failed to create native chart: ${errorMsg}`);
    return {
      success: false,
      message: `Failed to create chart: ${errorMsg}`,
    };
  }
}
